import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import RequireRole from "./features/auth/components/RequireRole";
import RequirePermission from "./features/auth/components/RequirePermission";
import Unauthorized from "./pages/common/Unauthorized";
import { CartProvider } from "./context/CartContext";
import TawkToWidget from "./components/client/TawkToWidget";
import { ScrollToTop } from "./components/kilDashboard/common/ScrollToTop";
import Loading from "./components/common/Loading";

// Eager: shell auth + parcours achat critique (first paint)
import Home from "./pages/client/Home";
import Cart from "./pages/client/Cart";
import Checkout from "./pages/client/Checkout";
import CheckoutSuccess from "./pages/client/CheckoutSuccess";
import Login from "./pages/dashboard/auth/Login";
import ForgotPassword from "./pages/dashboard/auth/ForgotPassword";
import ResetPassword from "./pages/dashboard/auth/ResetPassword";
import UserRegisterPage from "./pages/client/auth/Register";
import UserLoginPage from "./pages/client/auth/Login";
import ResetClientPassword from "./pages/client/auth/ResetPassword";
import KilLayout from "./layouts/kil-dashboard/AppLayout";
import AdminLayout from "./layouts/admin/AdminLayout";

// Lazy: pages lourdes (dashboards, finances, documents, listes)
const Categories = lazy(() => import("./pages/client/Categories"));
const ProductDetail = lazy(() => import("./pages/client/ProductDetail"));
const Boutiques = lazy(() => import("./pages/client/Boutiques"));
const BecomeSeller = lazy(() => import("./pages/client/BecomeSeller"));
const Account = lazy(() => import("./pages/client/Account"));
const ShopDetails = lazy(() => import("./pages/client/ShopDetails"));
const PromotionsPage = lazy(() => import("./pages/client/PromotionsPage"));
const SearchResults = lazy(() => import("./pages/client/SearchResults"));
const FAQPage = lazy(() => import("./pages/client/HelpPage"));
const PrivacyPolicy = lazy(() => import("./pages/client/PrivacyPolicy"));
const TermsOfSale = lazy(() => import("./pages/client/TermsOfSale"));
const ReturnPolicy = lazy(() => import("./pages/client/ReturnPolicy"));

const KilDashboardHome = lazy(() => import("./pages/dashboard/Home"));
const KilDashboardOrderList = lazy(() =>
  import("./pages/dashboard/orders/OrderList")
);
const OrderDetail = lazy(() => import("./pages/dashboard/orders/OrderDetail"));
const Invoice = lazy(() => import("./pages/dashboard/orders/Invoice"));
const MyRevenues = lazy(() => import("./pages/dashboard/revenues/MyRevenues"));
const ShopReviews = lazy(() => import("./pages/dashboard/reviews/ShopReviews"));
const KilDashboardProductList = lazy(() =>
  import("./features/seller/products/pages/ProductList")
);
const AddProduct = lazy(() =>
  import("./features/seller/products/pages/AddProduct")
);
const EditProduct = lazy(() =>
  import("./features/seller/products/pages/EditProduct")
);
const ProductDetails = lazy(() =>
  import("./features/seller/products/pages/ProductDetails")
);
const CategoryDetail = lazy(() =>
  import("./pages/dashboard/products/categories/CategoryDetail")
);
const PromoCodesPage = lazy(() =>
  import("./features/seller/promos/pages/PromoCodesPage")
);
const AdminSettings = lazy(() => import("./pages/dashboard/settings"));
const AccessRoles = lazy(() =>
  import("./pages/dashboard/acces-roles/acces-roles")
);
const MyOrders = lazy(() => import("./pages/dashboard/myorders/MyOrders"));
const MyFavorites = lazy(() =>
  import("./pages/dashboard/myfavorites/MyFavorites")
);

const AdminHome = lazy(() => import("./pages/admin/Home"));
const Clients = lazy(() => import("./pages/admin/users/Client/Clients"));
const ClientDetailsPage = lazy(() =>
  import("./pages/admin/users/Client/ClientDetailsPage")
);
const Orders = lazy(() => import("./pages/admin/Orders"));
const BannerPromoPage = lazy(() => import("./pages/admin/cms/BannerPromo"));
const SliderAccueilPage = lazy(() => import("./pages/admin/cms/SliderAccueil"));
const Commissions = lazy(() => import("./pages/admin/finances/Commissions"));
const Settings = lazy(() => import("./pages/admin/Settings"));
const StockMaintenanceSettings = lazy(() =>
  import("./pages/admin/StockMaintenanceSettings")
);
const RolesPermissions = lazy(() => import("./pages/admin/RolesPermissions"));
const CategoriesAdmin = lazy(() => import("./pages/admin/Categories"));
const Vendors = lazy(() => import("./features/admin/vendors/pages/Vendors"));
const VendorValidationPage = lazy(() =>
  import("./features/admin/vendors/pages/VendorValidationPage")
);
const AdminShopDetails = lazy(() =>
  import("./features/admin/vendors/pages/AdminShopDetails")
);
const ProductValidation = lazy(() =>
  import("./pages/admin/products/ProductValidation")
);
const AdminProductDetail = lazy(() =>
  import("./pages/admin/products/AdminProductDetail")
);
const AccessRolesPage = lazy(() => import("./pages/admin/Role/AccessRolesPage"));
const DocumentsGenerale = lazy(() =>
  import("./pages/admin/documents/DocumentsGenerale")
);
const AdminPlatformOrderList = lazy(() =>
  import("./pages/admin/transactions/AdminPlatformOrderList")
);
const FaqMessages = lazy(() => import("./pages/admin/FaqMessages"));
const AdminReviewsModeration = lazy(() =>
  import("./pages/admin/reviews/AdminReviewsModeration")
);

function RouteFallback() {
  return (
    <div className="min-h-[40vh] flex items-center justify-center">
      <Loading text="Chargement de la page..." />
    </div>
  );
}

function AppRouter() {
  return (
    <Router basename={process.env.PUBLIC_URL}>
      <CartProvider>
        <ScrollToTop />
        <TawkToWidget />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/checkout/success" element={<CheckoutSuccess />} />
            <Route path="/categories" element={<Categories />} />
            <Route path="/search" element={<SearchResults />} />
            <Route path="/product/:slug" element={<ProductDetail />} />
            <Route path="/login" element={<UserLoginPage />} />
            <Route path="/register" element={<UserRegisterPage />} />
            <Route path="/vendor/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetClientPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/boutiques" element={<Boutiques />} />
            <Route path="/boutiques/:slug" element={<ShopDetails />} />
            <Route path="/become-seller" element={<BecomeSeller />} />
            <Route path="/account" element={<Account />} />
            <Route path="/promotions" element={<PromotionsPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-sale" element={<TermsOfSale />} />
            <Route path="/return-policy" element={<ReturnPolicy />} />
            <Route path="/unauthorized" element={<Unauthorized />} />

            {/* Dashboard vendeur */}
            <Route
              path="/dashboard"
              element={
                <RequireRole allowedRoles={["SELLER"]}>
                  <KilLayout />
                </RequireRole>
              }
            >
              <Route
                index
                element={
                  <RequirePermission permission="dashboard:read">
                    <KilDashboardHome />
                  </RequirePermission>
                }
              />
              <Route
                path="products"
                element={
                  <RequirePermission permission="products:read">
                    <KilDashboardProductList />
                  </RequirePermission>
                }
              />
              <Route
                path="reviews"
                element={
                  <RequirePermission permission="reviews:read">
                    <ShopReviews />
                  </RequirePermission>
                }
              />
              <Route
                path="promos"
                element={
                  <RequirePermission permission="promos:read">
                    <PromoCodesPage />
                  </RequirePermission>
                }
              />
              <Route
                path="products/add"
                element={
                  <RequirePermission permission="products:create">
                    <AddProduct />
                  </RequirePermission>
                }
              />
              <Route
                path="orders"
                element={
                  <RequirePermission permission="orders:read">
                    <KilDashboardOrderList />
                  </RequirePermission>
                }
              />
              <Route
                path="revenues"
                element={
                  <RequirePermission permission="revenues:read">
                    <MyRevenues />
                  </RequirePermission>
                }
              />
              <Route
                path="orders/:orderId"
                element={
                  <RequirePermission permission="orders:read">
                    <OrderDetail />
                  </RequirePermission>
                }
              />
              <Route
                path="orders/:orderId/invoice"
                element={
                  <RequirePermission permission="orders:invoice">
                    <Invoice />
                  </RequirePermission>
                }
              />
              <Route
                path="categories/:categoryId"
                element={
                  <RequirePermission permission="products:read">
                    <CategoryDetail />
                  </RequirePermission>
                }
              />
              <Route
                path="products/edit/:id"
                element={
                  <RequirePermission permission="products:update">
                    <EditProduct />
                  </RequirePermission>
                }
              />
              <Route
                path="products/:id"
                element={
                  <RequirePermission permission="products:read">
                    <ProductDetails />
                  </RequirePermission>
                }
              />
              <Route
                path="settings"
                element={
                  <RequirePermission permission="settings:read">
                    <AdminSettings />
                  </RequirePermission>
                }
              />
              <Route
                path="access-roles"
                element={
                  <RequirePermission permission="access-roles:read">
                    <AccessRoles />
                  </RequirePermission>
                }
              />
              <Route path="my-orders" element={<MyOrders />} />
              <Route path="my-favorites" element={<MyFavorites />} />
            </Route>

            {/* Admin Dashboard */}
            <Route
              path="/admin-dashboard"
              element={
                <RequireRole allowedRoles={["ADMIN"]}>
                  <AdminLayout />
                </RequireRole>
              }
            >
              <Route
                index
                element={
                  <RequirePermission permission="dashboard:read">
                    <AdminHome />
                  </RequirePermission>
                }
              />
              <Route
                path="users"
                element={
                  <RequirePermission permission="clients:read">
                    <Clients />
                  </RequirePermission>
                }
              />
              <Route
                path="users/:id"
                element={
                  <RequirePermission permission="clients:read">
                    <ClientDetailsPage />
                  </RequirePermission>
                }
              />
              <Route path="roles-permissions" element={<RolesPermissions />} />
              <Route path="orders" element={<Orders />} />
              <Route
                path="products"
                element={
                  <RequirePermission permission="products-validation:read">
                    <ProductValidation />
                  </RequirePermission>
                }
              />
              <Route
                path="reviews"
                element={
                  <RequirePermission permission="reviews-moderation:read">
                    <AdminReviewsModeration />
                  </RequirePermission>
                }
              />
              <Route
                path="/admin-dashboard/cms/banner"
                element={
                  <RequirePermission permission="cms-banner:read">
                    <BannerPromoPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/admin-dashboard/cms/slider"
                element={
                  <RequirePermission permission="cms-slider:read">
                    <SliderAccueilPage />
                  </RequirePermission>
                }
              />
              <Route
                path="finances/commissions"
                element={
                  <RequirePermission permission="commissions:read">
                    <Commissions />
                  </RequirePermission>
                }
              />
              <Route
                path="stock-maintenance"
                element={
                  <RequirePermission permission="settings:read">
                    <StockMaintenanceSettings />
                  </RequirePermission>
                }
              />
              <Route
                path="settings"
                element={
                  <RequirePermission permission="settings:read">
                    <Settings />
                  </RequirePermission>
                }
              />
              <Route
                path="categories"
                element={
                  <RequirePermission permission="categories:read">
                    <CategoriesAdmin />
                  </RequirePermission>
                }
              />
              <Route
                path="vendors"
                element={
                  <RequirePermission permission="vendors:read">
                    <Vendors />
                  </RequirePermission>
                }
              />
              <Route
                path="access-roles"
                element={
                  <RequirePermission permission="access-roles:read">
                    <AccessRolesPage />
                  </RequirePermission>
                }
              />
              <Route
                path="all-orders"
                element={
                  <RequirePermission permission="all-orders:read">
                    <AdminPlatformOrderList />
                  </RequirePermission>
                }
              />
              <Route
                path="all-orders/:orderId"
                element={
                  <RequirePermission permission="all-orders:read">
                    <OrderDetail />
                  </RequirePermission>
                }
              />
              <Route
                path="all-orders/:orderId/invoice"
                element={
                  <RequirePermission permission="all-orders:invoice">
                    <Invoice />
                  </RequirePermission>
                }
              />
              <Route
                path="products/:slug"
                element={
                  <RequirePermission permission="products-validation:read">
                    <AdminProductDetail />
                  </RequirePermission>
                }
              />
              <Route
                path="vendors/validation/:id"
                element={
                  <RequirePermission permission="vendors:read">
                    <VendorValidationPage />
                  </RequirePermission>
                }
              />
              <Route
                path="vendors/details"
                element={
                  <RequirePermission permission="vendors:read">
                    <AdminShopDetails />
                  </RequirePermission>
                }
              />
              <Route
                path="documents-generale"
                element={
                  <RequirePermission permission="documents-generale:read">
                    <DocumentsGenerale />
                  </RequirePermission>
                }
              />
              <Route
                path="faq-messages"
                element={
                  <RequirePermission permission="faq-messages:read">
                    <FaqMessages />
                  </RequirePermission>
                }
              />
            </Route>
          </Routes>
        </Suspense>
      </CartProvider>
    </Router>
  );
}

export default AppRouter;
